import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type {
  IAffiliateCommission,
  IAffiliateCommissionFilter,
  IAffiliateCommissionsDetails
} from 'v2/interfaces/reports/affiliateCommissions';

import apiService from '../api';
import type { ApiService } from '../api';

const today = formatDate(new Date(), 'dd/MM/yyyy');

export const initialFilter: IAffiliateCommissionFilter = {
  affiliates: [],
  date: [today, today]
};

const initialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10
};

class AffiliateCommissionsService {
  private filters$ = new Rx.BehaviorSubject<IAffiliateCommissionFilter>(initialFilter);

  private loadingCommissions$ = new Rx.BehaviorSubject<boolean>(true);
  private commissions$ = new Rx.BehaviorSubject<IAffiliateCommission[]>([]);
  private loadingAffiliateCommissionsDetails$ = new Rx.BehaviorSubject<boolean>(true);
  private affiliateCommissionsDetails$ = new Rx.BehaviorSubject<IAffiliateCommissionsDetails>(
    {} as IAffiliateCommissionsDetails
  );

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(initialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private totalGain$ = new Rx.BehaviorSubject<number>(0);

  constructor(private apiService: ApiService) {}

  public setFilter(filter: IAffiliateCommissionFilter): void {
    this.loadingCommissions$.next(true);

    this.filters$.pipe(take(1)).subscribe((currentFilter: IAffiliateCommissionFilter) => {
      this.filters$.next({ ...currentFilter, ...filter });
    });
  }

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public getTotalGain = () => this.totalGain$;

  public getFilters = (): Rx.Observable<IAffiliateCommissionFilter> => this.filters$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public isLoading = () => this.loadingCommissions$;

  public getCommissions = (): Rx.Observable<IAffiliateCommission[]> => this.commissions$;

  public formatFilter = (filter: IAffiliateCommissionFilter) => {
    const newFilter: Partial<IAffiliateCommissionFilter> = {};
    newFilter.created_at = filter?.date[0];
    newFilter.created_at_final = filter?.date[1];
    newFilter.affiliate_id = filter?.affiliates.map(affiliate => affiliate.id);

    return newFilter;
  };

  public loadCommissions() {
    return this.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter => this.resetPage().pipe(map(params => ({ ...filter, ...params })))),
      debounceTime(10),
      switchMap(filter => {
        this.loadingCommissions$.next(true);
        return this.apiService.get('reports/sales/commissions/affiliates', filter);
      }),
      tap(response => {
        this.commissions$.next(response.data);
        this.totalGain$.next(response.total_gain);
        this.paginationResponse$.next(response.pagination);
        this.totalItems$.next(response.pagination.total_items);
        this.loadingCommissions$.next(false);
      })
    );
  }

  public export() {
    return this.apiService.get('reports/sales/commissions/affiliates', {
      ...this.formatFilter(this.filters$.value),
      export: true
    });
  }

  public cleanFilter() {
    this.filters$.next(initialFilter);
  }

  public requestAffiliateCommissionsDetails(commissionedId: number) {
    this.loadingAffiliateCommissionsDetails$.next(true);
    return this.apiService
      .get(`reports/sales/commissions/affiliates/${commissionedId}`, {
        ...this.formatFilter(this.filters$.value),
        export: true
      })
      .pipe(
        map(response => response.data),
        tap(data => {
          this.affiliateCommissionsDetails$.next(data);
          this.loadingAffiliateCommissionsDetails$.next(false);
        })
      );
  }

  public getAffiliateCommissionsDetails() {
    return this.affiliateCommissionsDetails$;
  }

  public getLoadingAffiliateCommissionsDetails() {
    return this.loadingAffiliateCommissionsDetails$;
  }
}

export const affiliateCommissionsService = new AffiliateCommissionsService(apiService);
